import { clampRect, composeRect } from '@/core/geometry'
import type { LocatedMetricRegion, NormalizedRect, RecognitionMetricKey } from '@/domain/types'

export const HUD_REFERENCE = { width: 1364, height: 39 } as const
export const HUD_BAR_METRICS = ['hp', 'mp', 'exp'] as const
/** Short tracks are a different layout, not a scaled version of the wide HUD. */
export const COMPACT_HUD = {
  offsets: [0, 108, 221],
  widths: [105, 105, 115],
  borderHeight: 15,
  barHeight: 12,
  anchorX: 220,
  anchorY: 18,
  width: 1280,
  height: 37,
  structure: {
    level: { x: 7, y: 7, width: 72, height: 28 },
    jobName: { x: 84, y: 3, width: 126, height: 16 },
  },
} as const
export type HudBarMetric = (typeof HUD_BAR_METRICS)[number]

interface PixelBox {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

/** Full HUD slot used for structure search, invalidation, and wide OCR fallback. */
export const HUD_STRUCTURE: Readonly<Record<'level' | 'jobName' | HudBarMetric, PixelBox>> = {
  level: { x: 8, y: 7, width: 86, height: 28 },
  jobName: { x: 106, y: 5, width: 210, height: 14 },
  hp: { x: 464, y: 3, width: 167, height: 32 },
  mp: { x: 632, y: 3, width: 167, height: 32 },
  exp: { x: 808, y: 3, width: 167, height: 32 },
}

/** Compact OCR crop: the complete text line, excluding the colored track. */
export const HUD_TEXT: Readonly<Record<'level' | 'jobName' | HudBarMetric, PixelBox>> = {
  level: HUD_STRUCTURE.level,
  jobName: HUD_STRUCTURE.jobName,
  hp: { x: 464, y: 3, width: 167, height: 16 },
  mp: { x: 632, y: 3, width: 167, height: 16 },
  exp: { x: 808, y: 3, width: 167, height: 16 },
}

export const HUD_BAR: Readonly<Record<HudBarMetric, PixelBox>> = {
  hp: { x: 465, y: 19, width: 165, height: 14 },
  mp: { x: 633, y: 19, width: 165, height: 14 },
  exp: { x: 809, y: 19, width: 165, height: 14 },
}

export const FRAME_ROI = {
  mesos: { x: 0.79, y: 0.486, width: 0.125, height: 0.034 },
  mapName: { x: 48 / 1366, y: 43 / 768, width: 150 / 1366, height: 18 / 768 },
} as const

export function isHudBarMetric(metric: RecognitionMetricKey): metric is HudBarMetric {
  return metric === 'hp' || metric === 'mp' || metric === 'exp'
}

export function normalizedHudBox(box: PixelBox): NormalizedRect {
  return {
    x: box.x / HUD_REFERENCE.width,
    y: box.y / HUD_REFERENCE.height,
    width: box.width / HUD_REFERENCE.width,
    height: box.height / HUD_REFERENCE.height,
  }
}

export const PRESET_TEXT_ROI: Readonly<Record<RecognitionMetricKey, NormalizedRect>> = {
  level: normalizedHudBox(HUD_TEXT.level),
  jobName: normalizedHudBox(HUD_TEXT.jobName),
  hp: normalizedHudBox(HUD_TEXT.hp),
  mp: normalizedHudBox(HUD_TEXT.mp),
  exp: normalizedHudBox(HUD_TEXT.exp),
  mesos: FRAME_ROI.mesos,
  mapName: FRAME_ROI.mapName,
}

export const PRESET_STRUCTURE_ROI: Readonly<Record<RecognitionMetricKey, NormalizedRect>> = {
  level: normalizedHudBox(HUD_STRUCTURE.level),
  jobName: normalizedHudBox(HUD_STRUCTURE.jobName),
  hp: normalizedHudBox(HUD_STRUCTURE.hp),
  mp: normalizedHudBox(HUD_STRUCTURE.mp),
  exp: normalizedHudBox(HUD_STRUCTURE.exp),
  mesos: FRAME_ROI.mesos,
  mapName: FRAME_ROI.mapName,
}

export function barWithinStructure(metric: HudBarMetric): NormalizedRect {
  const structure = HUD_STRUCTURE[metric]
  const bar = HUD_BAR[metric]
  return {
    x: (bar.x - structure.x) / structure.width,
    y: (bar.y - structure.y) / structure.height,
    width: bar.width / structure.width,
    height: bar.height / structure.height,
  }
}

export function compactTextRect(
  metric: RecognitionMetricKey,
  structure: NormalizedRect,
  barRect?: NormalizedRect,
): NormalizedRect {
  if (!isHudBarMetric(metric)) return structure
  if (barRect) {
    return clampRect({
      x: structure.x,
      y: structure.y,
      width: structure.width,
      height: Math.max(0.002, barRect.y - structure.y),
    })
  }
  const text = HUD_TEXT[metric]
  const slot = HUD_STRUCTURE[metric]
  return clampRect({
    x: structure.x,
    y: structure.y,
    width: structure.width,
    height: structure.height * (text.height / slot.height),
  })
}

export function displayedMetricRect(
  metric: RecognitionMetricKey,
  region: LocatedMetricRegion | undefined,
  hud: NormalizedRect,
  override?: NormalizedRect,
  fallbackRois: Readonly<Record<RecognitionMetricKey, NormalizedRect>> = PRESET_TEXT_ROI,
): NormalizedRect {
  if (override) return override
  if (region?.textRect) return region.textRect
  if (region) return compactTextRect(metric, region.rect, region.barRect)
  const definitionSpace = metric === 'mesos' || metric === 'mapName' ? 'frame' : 'hud'
  const text = fallbackRois[metric]
  return definitionSpace === 'frame' ? text : composeRect(hud, text)
}

export function withCompactTextRect(
  metric: RecognitionMetricKey,
  region: LocatedMetricRegion,
): LocatedMetricRegion {
  if (region.textRect) return region
  return { ...region, textRect: compactTextRect(metric, region.rect, region.barRect) }
}

export function withCompactAutoRois(
  autoRois: Partial<Record<RecognitionMetricKey, LocatedMetricRegion>> | undefined,
): Partial<Record<RecognitionMetricKey, LocatedMetricRegion>> | undefined {
  if (!autoRois) return autoRois
  return Object.fromEntries(
    Object.entries(autoRois).map(([metric, region]) => [
      metric,
      withCompactTextRect(metric as RecognitionMetricKey, region),
    ]),
  )
}
